import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import './TestPracticeSetsStart.css';
import CircularTimer from '../CircularTimer';
import studentApi from '../StudentApi';
import { FiEdit, FiArrowLeft, FiArrowRight, FiSend, FiBookmark } from 'react-icons/fi';
import Swal from 'sweetalert2';

const DURATION_MINUTES = 60;

function TestPracticeSetsStart() {
    const navigate = useNavigate();
    const { practiceSetId } = useParams();

    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(DURATION_MINUTES * 60);
    const [markedForReview, setMarkedForReview] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const examIdRef = useRef(null);
    const TOTAL_QUESTIONS = questions.length;

    // Fetch questions
    useEffect(() => {
        if (!practiceSetId) {
            alert("Missing practice set ID!");
            navigate(-1);
            return;
        }

        const fetchQuestions = async () => {
            try {
                setLoading(true);
                const res = await studentApi.get(`/practice-set/${practiceSetId}/questions`);
                const extracted = res.data.data.map(item => ({
                    id: item.id,
                    exam_id: item.exam_id,
                    question: item.question_text,
                    options: [item.option_a, item.option_b, item.option_c, item.option_d],
                    correct_option: item.correct_option,
                    type: item.question_type === 'MCQ_RADIO' ? 'single' : 'multiple'
                }));
                setQuestions(extracted);
                if (extracted.length > 0) examIdRef.current = extracted[0].exam_id;
            } catch (err) {
                console.error(err);
                setError("Failed to load questions.");
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [practiceSetId, navigate]);

    // Timer
    useEffect(() => {
        if (!questions.length) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    if (!isSubmitting) submitExamNow();
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [questions, isSubmitting]);

    // Local storage persistence
    const storageKey = `tps-progress-${practiceSetId}`;
    useEffect(() => {
        if (questions.length > 0) {
            localStorage.setItem(storageKey, JSON.stringify({ selectedAnswers, timeLeft, markedForReview }));
        }
    }, [selectedAnswers, timeLeft, markedForReview, storageKey, questions]);

    useEffect(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const { selectedAnswers, timeLeft, markedForReview } = JSON.parse(saved);
            setSelectedAnswers(selectedAnswers || {});
            setTimeLeft(timeLeft || DURATION_MINUTES * 60);
            setMarkedForReview(markedForReview || {});
        }
    }, [storageKey]);

    // Auto scroll question into view when changing
    useEffect(() => {
        document.querySelector(".tps-question-box")?.scrollIntoView({ behavior: "smooth" });
    }, [currentIndex]);

    // Attempted count logic
    const attemptedCount = Object.values(selectedAnswers).filter(ans =>
        Array.isArray(ans) ? ans.length > 0 : !!ans
    ).length;
    const unattemptedCount = TOTAL_QUESTIONS - attemptedCount;

    // Submit exam
    const submitExamNow = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const studentData = JSON.parse(localStorage.getItem('studentData') || '{}');
            const studentId = parseInt(studentData.id, 10);

            if (!studentId) {
                Swal.fire('Error', 'Student ID missing. Please login again.', 'error');
                navigate('/student-login');
                setIsSubmitting(false);
                return;
            }

            const answersPayload = questions.map((q, idx) => {
                const sel = selectedAnswers[idx];
                return q.type === 'single'
                    ? { question_id: q.id, selected_option: sel || null, selected_options: [] }
                    : { question_id: q.id, selected_option: null, selected_options: Array.isArray(sel) ? sel.map(String) : [] };
            });

            const payload = {
                student_id: studentId,
                exam_id: examIdRef.current,
                source_type: 'practice',
                answers: answersPayload
            };

            const res = await studentApi.post('/exam/submit', payload);

            Swal.fire({
                icon: 'success',
                title: 'Exam Submitted!',
                text: `Total Marks: ${res.data.total_marks}, Correct: ${res.data.correct}, Wrong: ${res.data.wrong}`,
            }).then(() => {
                navigate('/result', { state: { ...res.data, selectedAnswers, questions, examId: examIdRef.current } });
            });

        } catch (error) {
            console.error('Error submitting exam:', error.response || error.message);
            Swal.fire({
                icon: 'error',
                title: 'Failed to submit exam',
                text: error.response?.data?.message || 'Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Confirm before submit
    const confirmSubmit = () => {
        Swal.fire({
            title: "Submit Exam?",
            text: "You cannot change answers after submission.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Submit",
        }).then(result => {
            if (result.isConfirmed) submitExamNow();
        });
    };

    return (
        <MathJaxContext>
            <div className="tps-container">
                {loading ? (
                    <p>Loading questions...</p>
                ) : error ? (
                    <p className="tps-error">{error}</p>
                ) : questions.length === 0 ? (
                    <p>No questions found for this practice set.</p>
                ) : (
                    <div className="tps-exam-layout">
                        {/* Main Left Side */}
                        <div className="tps-main">

                            {/* Header */}
                            <div className="tps-header">
                                <h2><FiEdit size={24} color="#fd7e14" /> Practice Set Exam</h2>
                                <CircularTimer duration={DURATION_MINUTES * 60} timeLeft={timeLeft} />
                            </div>

                            {/* Question Box */}
                            <div className="tps-question-box">
                                <div className="tps-question-content">
                                    <h3>Q{currentIndex + 1}:</h3>
                                    <MathJax>
                                        <div dangerouslySetInnerHTML={{ __html: questions[currentIndex]?.question || '' }} />
                                    </MathJax>
                                </div>

                                {/* Options */}
                                <div className="tps-options">
                                    {questions[currentIndex]?.options?.map((opt, idx) => {
                                        const type = questions[currentIndex]?.type;
                                        const name = `tps-question-${currentIndex}`;
                                        const optionLetter = String.fromCharCode(65 + idx);
                                        const isSelected =
                                            type === "single"
                                                ? selectedAnswers[currentIndex] === optionLetter
                                                : selectedAnswers[currentIndex]?.includes(optionLetter);

                                        const handleChange = e => {
                                            if (type === 'single') {
                                                setSelectedAnswers({ ...selectedAnswers, [currentIndex]: e.target.value });
                                            } else {
                                                const existing = selectedAnswers[currentIndex] || [];
                                                setSelectedAnswers({
                                                    ...selectedAnswers,
                                                    [currentIndex]: e.target.checked
                                                        ? [...existing, e.target.value]
                                                        : existing.filter(item => item !== e.target.value)
                                                });
                                            }
                                        };

                                        return (
                                            <label key={idx} className={`tps-option-button ${isSelected ? 'selected' : ''}`}>
                                                <input
                                                    type={type === 'single' ? 'radio' : 'checkbox'}
                                                    name={name}
                                                    value={optionLetter}
                                                    checked={isSelected}
                                                    onChange={handleChange}
                                                    className="tps-input"
                                                />
                                                <MathJax>
                                                    <span>
                                                        <strong>{optionLetter}.</strong>{' '}
                                                        <span dangerouslySetInnerHTML={{ __html: opt }} />
                                                    </span>
                                                </MathJax>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            <div className="tps-navigation-buttons">
                                <button onClick={() => setCurrentIndex(i => i - 1)} disabled={currentIndex === 0}>
                                    <FiArrowLeft /> Previous
                                </button>
                                <button onClick={() => setCurrentIndex(i => i + 1)} disabled={currentIndex === TOTAL_QUESTIONS - 1}>
                                    Next <FiArrowRight />
                                </button>
                                <button
                                    onClick={() => setMarkedForReview(prev => ({ ...prev, [currentIndex]: !prev[currentIndex] }))}
                                    className={`tps-marked-review ${markedForReview[currentIndex] ? 'marked' : ''}`}
                                >
                                    <FiBookmark /> {markedForReview[currentIndex] ? 'Unmark Review' : 'Mark for Review'}
                                </button>
                                <button onClick={confirmSubmit} className='tps-submit' disabled={isSubmitting}>
                                    <FiSend /> Submit
                                </button>
                            </div>

                            {/* Progress Bar */}
                            <div className="tps-progress-chart">
                                <div className="tps-bar-container">
                                    <div
                                        className="tps-bar-fill-attempted"
                                        style={{ width: `${(attemptedCount / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                    <div
                                        className="tps-bar-fill-unattempted"
                                        style={{ width: `${(unattemptedCount / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                </div>
                            </div>
                            <div className="tps-progress-labels">
                                <span>✅ Attempted: {attemptedCount}</span>
                                <span>❌ Unattempted: {unattemptedCount}</span>
                            </div>
                        </div>

                        {/* Sidebar Right Side */}
                        <div className="tps-sidebar">
                            <div className="tps-question-tracker">
                                {questions.map((q, idx) => {
                                    const isAttempted =
                                        q.type === "single"
                                            ? !!selectedAnswers[idx]
                                            : (selectedAnswers[idx] && selectedAnswers[idx].length > 0);

                                    const isMarked = markedForReview[idx];
                                    const isCurrent = currentIndex === idx;

                                    let trackerClass = "tps-tracker-box";
                                    if (isAttempted) trackerClass += " attempted";
                                    if (!isAttempted) trackerClass += " unattempted";
                                    if (isMarked) trackerClass += " marked";
                                    if (isCurrent) trackerClass += " current";

                                    return (
                                        <button
                                            key={idx}
                                            className={trackerClass}
                                            onClick={() => setCurrentIndex(idx)}
                                        >
                                            {idx + 1}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MathJaxContext>
    );
}

export default TestPracticeSetsStart;
